import { useMemo } from 'react'
import styled from 'styled-components'
import Decimal from 'decimal.js'

import { PhantomTypeArgument, TypeArgument } from '@/shared/gen/_framework/reified'
import { DevInspectLpUnclaimedRewardsResult, PositionConfigInfo, Amount, CoinInfo, USDC } from '@kunalabs-io/kai'

import { IconInfo } from 'components/Icons/InfoIcon'
import { ReversedWrapper } from 'components/ReversedWrapper/ReversedWrapper'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { Typography } from 'components/Typography/Typography'
import { useTokenPrice } from 'utils/useTokenPrice'
import { getFormattedUsdValue } from 'utils/usdFormatters'

const SectionLabel = styled(Typography)`
  display: flex;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 16px;
  & > div {
    display: flex;
  }
  svg {
    cursor: pointer;
    margin-left: 6px;
    path {
      stroke: ${p => p.theme.colors.text.secondary};
    }
  }
`

const PositionBreakdownContainer = styled.div`
  padding: 20px 16px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background.secondary};
`

const PositionBreakdownContainerItem = styled.div`
  display: flex;
  justify-content: space-between;
`

const PositionBreakdownSymbol = styled(Typography)`
  display: flex;
  svg {
    margin-right: 8px;
    border-radius: 50%;
  }
`

const InfoContainer = styled.div`
  display: block;
  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    & > div {
      flex-basis: 50%;
    }
  }
`

interface Props {
  isReversedPair: boolean
  rewards: DevInspectLpUnclaimedRewardsResult | null
  configInfo?: PositionConfigInfo<string, string, TypeArgument>
  isWithinWithdraw?: boolean
}

interface RewardItemProps {
  coinInfo: CoinInfo<PhantomTypeArgument>
  amount: Amount
  isLast: boolean
}

interface FeeItemProps {
  coinInfo: CoinInfo<PhantomTypeArgument>
  amount: Amount
  marginBottom: string
}

const RewardItem = ({ coinInfo, amount, isLast }: RewardItemProps) => {
  const { isLoading, price } = useTokenPrice(coinInfo, USDC)

  const rewardsValueUsd = useMemo(() => {
    if (!price || isLoading) {
      return ''
    }

    return getFormattedUsdValue(new Decimal(0).plus(amount.toDecimal().mul(price.human)).toNumber())
  }, [price, isLoading, amount])

  return (
    <PositionBreakdownContainerItem key={coinInfo.typeName} style={isLast ? {} : { marginBottom: 20 }}>
      <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
        <img src={coinInfo.iconUrl} alt={coinInfo.displaySymbol} width={24} height={24} />
        &nbsp;
        {coinInfo.displaySymbol}
      </PositionBreakdownSymbol>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="description" fontWeight="semiBold">
          {amount.toDecimal().toString()}
        </Typography>
        <Typography variant="caption" color="secondary" style={{ marginLeft: 4 }}>
          {`(${rewardsValueUsd})`}
        </Typography>
      </div>
    </PositionBreakdownContainerItem>
  )
}

const FeeItem = ({ coinInfo, amount, marginBottom }: FeeItemProps) => {
  const { isLoading, price } = useTokenPrice(coinInfo, USDC)

  const feeValueUsd = useMemo(() => {
    if (!price || isLoading) {
      return ''
    }

    return getFormattedUsdValue(new Decimal(0).plus(amount.toDecimal().mul(price.human)).toNumber())
  }, [price, isLoading, amount])

  return (
    <PositionBreakdownContainerItem style={{ marginBottom: marginBottom }}>
      <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
        <img src={coinInfo.iconUrl} alt={coinInfo.displaySymbol} width={24} height={24} />
        &nbsp;
        {coinInfo.displaySymbol}
      </PositionBreakdownSymbol>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="description" fontWeight="semiBold">
          {amount.toDecimal().toString()}
        </Typography>
        <Typography variant="caption" color="secondary" style={{ marginLeft: 4 }}>
          {`(${feeValueUsd})`}
        </Typography>
      </div>
    </PositionBreakdownContainerItem>
  )
}

export const PendingFeesRewards = ({ isReversedPair, configInfo, rewards, isWithinWithdraw }: Props) => {
  const otherRewardsMap = useMemo(() => {
    const map = new Map<string, { coinInfo: CoinInfo<PhantomTypeArgument>; amount: Amount }>()
    if (!rewards) {
      return map
    }

    const mergeAmount = (coinType: CoinInfo<PhantomTypeArgument>, amount: bigint) => {
      const existingAmount = map.get(coinType.typeName)?.amount.int ?? 0n
      map.set(coinType.typeName, { coinInfo: coinType, amount: coinType.newAmount(existingAmount + amount) })
    }
    for (const [coinType, amount] of rewards.rewards) {
      mergeAmount(coinType, amount.int)
    }
    for (const [coinType, amount] of rewards.stashed) {
      mergeAmount(coinType, amount.int)
    }

    return map
  }, [rewards])

  if (!configInfo || !rewards) {
    return null
  }

  return (
    <InfoContainer>
      {/* Pending Fee section */}
      <div>
        <SectionLabel variant="body" color="secondary">
          Pending LP fees
          <Tooltip
            content="Unclaimed fees accrued from trades in the liquidity pool."
            position={isWithinWithdraw ? 'bottom' : 'top'}
            tooltipBoxStyles={{ marginLeft: 80 }}
          >
            <IconInfo width={16} height={16} />
          </Tooltip>
        </SectionLabel>
        <PositionBreakdownContainer>
          <ReversedWrapper
            childX={<FeeItem coinInfo={configInfo.X} amount={rewards.x} marginBottom={isReversedPair ? '' : '20px'} />}
            childY={<FeeItem coinInfo={configInfo.Y} amount={rewards.y} marginBottom={isReversedPair ? '20px' : ''} />}
            isReversed={isReversedPair}
          />
        </PositionBreakdownContainer>
      </div>
      {/* Pending Rewards section */}
      <div>
        <SectionLabel variant="body" color="secondary">
          Pending Rewards
          <Tooltip
            content="Extra incentives earned on top of swap fees for providing liquidity."
            position={isWithinWithdraw ? 'bottom' : 'top'}
          >
            <IconInfo width={16} height={16} />
          </Tooltip>
        </SectionLabel>
        <PositionBreakdownContainer>
          {Array.from(otherRewardsMap.values()).map((entry, index, array) => (
            <RewardItem
              key={entry.coinInfo.typeName}
              coinInfo={entry.coinInfo}
              amount={entry.amount}
              isLast={index === array.length - 1}
            />
          ))}
        </PositionBreakdownContainer>
      </div>
    </InfoContainer>
  )
}
